<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{params.title}}</div>
    <div slot="body">
      <dialog-selected-tips :name="$t('compute.text_113')" :count="params.data.length" :action="params.title" />
      <dialog-table :data="params.data" :columns="columns" />
      <a-form
        :form="form.fc">
        <a-form-item :label="$t('compute.text_492', [params.resourceType === 'server_container' ? this.$t('dictionary.server_container') : this.$t('dictionary.server')])" v-bind="formItemLayout" :extra="$t('compute.text_493')">
          <a-select v-decorator="decorators.guest">
            <a-select-option v-for="item in guestesOpts" :key="item.id">
              {{item.name}}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item v-if="showGpuType" :label="$t('gpu.device_type')" v-bind="formItemLayout">
          <base-select v-decorator="decorators.gpuType" :options="gpuTypeOptions" />
        </a-form-item>
        <a-form-item
          v-if="showMemoryRequest"
          :label="$t('compute.pci.memory_request')"
          :extra="showMthreadsMemory ? `${$t('compute.pci.memory_request.mthreads_sgpu_hint')} ${$t('compute.pci.mthreads_sgpu_single_card')}` : undefined"
          v-bind="formItemLayout">
          <base-select
            v-if="showMthreadsMemory"
            v-decorator="decorators.memoryRequest"
            :options="mthreadsMemoryOptions"
            :filterable="false"
            :select-props="{ placeholder: $t('compute.pci.memory_request.mthreads_sgpu_placeholder'), allowClear: false }"
            @change="onMemoryRequestChange" />
          <a-tooltip
            v-else
            :title="memoryRangeTooltip"
            placement="top"
            :get-popup-container="getTooltipContainer">
            <disk-size-input
              v-decorator="decorators.memoryRequest"
              :min="1"
              :max="memorySizeMb || Infinity"
              :step="1"
              :units="['MB', 'GB']"
              value-unit="MB"
              default-unit="GB"
              @change="onMemoryRequestChange"
              @unitChange="onMemoryUnitChange" />
          </a-tooltip>
        </a-form-item>
        <a-form-item v-if="isShowAutoStart" :label="$t('compute.text_494')" v-bind="formItemLayout" :extra="$t('compute.text_495')">
          <a-switch :checkedChildren="$t('compute.text_115')" :unCheckedChildren="$t('compute.text_116')" v-decorator="decorators.autoStart" />
        </a-form-item>
      </a-form>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import {
  GPU_TYPE_OPTION_MAP,
  getGpuTypeSelectOptions,
  resolveGpuTypeBySharingMode,
  isMthreadsVendor,
  mthreadsSgpuMemoryOptions,
  normalizeMthreadsSgpuMemory,
} from '@Compute/constants'
import DiskSizeInput from '@/sections/DiskSizeInput'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'

export default {
  name: 'GpuAttachServerDialog',
  components: {
    DiskSizeInput,
  },
  mixins: [DialogMixin, WindowsMixin],
  data () {
    return {
      loading: false,
      scope: this.$store.getters.scope,
      form: {
        fc: this.$form.createForm(this, {
          onValuesChange: (props, values) => {
            Object.keys(values).forEach((key) => {
              this.$set(this.form.fd, key, values[key])
            })
          },
        }),
        fd: {},
      },
      decorators: {
        guest: [
          'guest',
          {
            rules: [
              {
                required: true,
                message: this.$t('compute.text_496', [
                  this.$t('dictionary.server'),
                ]),
              },
            ],
          },
        ],
        gpuType: [
          'gpu_type',
          {
            initialValue: resolveGpuTypeBySharingMode(
              GPU_TYPE_OPTION_MAP.HPC.value,
              this.params.data[0]?.sharing_mode,
            ),
          },
        ],
        memoryRequest: [
          'memory_request',
          {
            rules: [
              { required: true, message: this.$t('compute.pci.memory_request.required') },
              { validator: this.validateMemoryRequest },
            ],
          },
        ],
        autoStart: [
          'autoStart',
          {
            valuePropName: 'checked',
            initialValue: true,
          },
        ],
      },
      guestesOpts: [],
      memoryUnit: 'GB',
      formItemLayout: {
        wrapperCol: {
          span: 18,
        },
        labelCol: {
          span: 6,
        },
      },
    }
  },
  computed: {
    currentDevice () {
      return this.params.data[0] || {}
    },
    columns () {
      const ret = []
      this.params.columns.map(item => {
        if (['name', 'dev_type', 'model', 'host'].indexOf(item.field) !== -1) {
          ret.push(item)
        }
      })
      return ret
    },
    curGuest () {
      return this.guestesOpts.find(o => o.id === this.form.fd.guest)
    },
    isGuestRunning () {
      return this.curGuest?.status === 'running'
    },
    isShowAutoStart () {
      return this.curGuest !== undefined && !this.isGuestRunning
    },
    gpuTypeOptions () {
      return getGpuTypeSelectOptions(this.currentDevice.sharing_mode)
    },
    showGpuType () {
      return this.currentDevice.dev_type === 'GPU'
    },
    showMemoryRequest () {
      return this.currentDevice.sharing_mode === 'HAMI'
    },
    showMthreadsMemory () {
      return this.showMemoryRequest && isMthreadsVendor(this.currentDevice.vendor)
    },
    mthreadsMemoryOptions () {
      return mthreadsSgpuMemoryOptions(this.memorySizeMb)
    },
    memorySizeMb () {
      return this.currentDevice.memory_size_mb || this.currentDevice.memory_size || undefined
    },
    memoryRangeTooltip () {
      return this.getMemoryRangeText(this.memorySizeMb, this.memoryUnit || 'GB')
    },
  },
  created () {
    const params = {
      host_id: this.params.data[0].host_id,
      filter: this.params.resourceType === 'server_container' ? 'hypervisor.in(pod)' : 'hypervisor.in(kvm)',
      scope: this.scope,
      limit: 0,
    }
    new this.$Manager('servers').list({ params })
      .then((res) => {
        this.guestesOpts = res.data.data.filter(val => this.params.resourceType === 'server_container' ? val.status === 'ready' : val.status === 'ready' || val.status === 'running')
      })
  },
  methods: {
    getTooltipContainer () {
      return document.body
    },
    getMemoryRangeText (maxMb, unit = 'GB') {
      if (!maxMb) return ''
      const factor = unit === 'GB' ? 1024 : 1
      const min = 1 / factor
      const max = maxMb / factor
      const fmt = (n) => (Number.isInteger(n) ? n : Number(n.toFixed(3)))
      return this.$t('compute.pci.memory_request.range', [fmt(min), fmt(max), unit])
    },
    validateMemoryRequest (rule, value, callback) {
      if (value === undefined || value === null || value === '') {
        callback()
        return
      }
      const num = Number(value)
      if (!Number.isInteger(num) || num <= 0) {
        callback(new Error(this.$t('compute.pci.memory_request.invalid')))
        return
      }
      if (this.showMthreadsMemory) {
        const allowed = this.mthreadsMemoryOptions.some(item => item.key === num)
        if (!allowed) {
          callback(new Error(this.$t('compute.pci.memory_request.mthreads_sgpu_bucket')))
          return
        }
        callback()
        return
      }
      if (this.memorySizeMb && num > this.memorySizeMb) {
        callback(new Error(this.getMemoryRangeText(this.memorySizeMb, this.memoryUnit || 'GB')))
        return
      }
      callback()
    },
    clampMemoryRequest (val) {
      if (val === undefined || val === null || val === '') return val
      const num = Number(val)
      if (!Number.isFinite(num)) return val
      if (this.memorySizeMb && num > this.memorySizeMb) return this.memorySizeMb
      if (num < 1) return 1
      return Math.round(num)
    },
    onMemoryRequestChange (val) {
      const next = this.showMthreadsMemory
        ? normalizeMthreadsSgpuMemory(val, this.memorySizeMb)
        : this.clampMemoryRequest(val)
      if (next === undefined) {
        this.form.fc.setFields({ memory_request: { value: undefined } })
        this.$delete(this.form.fd, 'memory_request')
        return
      }
      this.form.fc.setFieldsValue({ memory_request: next })
      this.$set(this.form.fd, 'memory_request', next)
    },
    onMemoryUnitChange (unit) {
      this.memoryUnit = unit
    },
    validateForm () {
      return new Promise((resolve, reject) => {
        this.form.fc.validateFields((err, values) => {
          if (!err) {
            resolve(values)
          } else {
            reject(err)
          }
        })
      })
    },
    doAttach (data) {
      const attachData = {
        auto_start: this.isShowAutoStart ? data.autoStart : false,
        device: this.params.data[0].id,
      }
      if (this.currentDevice.sharing_mode) {
        attachData.sharing_mode = this.currentDevice.sharing_mode
      }
      if (this.showGpuType && data.gpu_type) {
        attachData.gpu_type = data.gpu_type
      }
      if (this.showMemoryRequest && data.memory_request) {
        attachData.memory_request = data.memory_request
      }
      return new this.$Manager('servers').performAction({
        id: data.guest,
        action: 'attach-isolated-device',
        data: attachData,
      })
    },
    async handleConfirm () {
      this.loading = true
      try {
        const values = await this.validateForm()
        this.loading = true
        await this.doAttach(values)
        this.loading = false
        this.params.refresh()
        this.cancelDialog()
      } catch (error) {
        this.loading = false
      }
    },
  },
}
</script>
